import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/components/Layout";
import { api, apiErr, fmt } from "@/lib/api";
import { Button, Input, Label, Card, Modal } from "@/components/kit";
import { Plus, Pencil, Trash2, User, Mail, KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", password: "", role: "employee" };

export default function Team() {
  const { user: me } = useAuth();
  const isOwner = me?.role === "owner";
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwResult, setPwResult] = useState(null);
  const [pwBusy, setPwBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const openPw = (u) => { setPwUser(u); setPwValue(""); setPwResult(null); setCopied(false); };
  const setPassword = async (e) => {
    e.preventDefault();
    if (pwValue && pwValue.length < 6) return toast.error("Lösenordet måste vara minst 6 tecken");
    setPwBusy(true);
    try {
      const { data } = await api.post(`/users/${pwUser.id}/set-password`, { password: pwValue || undefined });
      setPwResult(data);
      toast.success("Lösenord satt – ge det till den anställde");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setPwBusy(false); }
  };
  const copyPw = () => {
    if (!pwResult?.password) return;
    navigator.clipboard.writeText(pwResult.password).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  const [credCopied, setCredCopied] = useState(false);
  const copyCreds = () => {
    if (!pwResult) return;
    const text = `Inloggning till ${document.title || "appen"}:\nLänk: ${window.location.origin}\nE-post: ${pwResult.email}\nLösenord: ${pwResult.password}`;
    navigator.clipboard.writeText(text).then(() => { setCredCopied(true); setTimeout(() => setCredCopied(false), 1500); });
  };

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`/users/${editId}`, { name: form.name, password: form.password || undefined, role: isOwner ? form.role : undefined });
        toast.success("Sparat");
      } else {
        const { data } = await api.post("/users", { name: form.name, email: form.email });
        if (data.invited) toast.success(`Inbjudan mejlad till ${data.email}`);
        else toast.warning(`Konto skapat, men mejl misslyckades. Tillfälligt lösenord: ${data.temp_password}`);
      }
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const invite = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/invite`);
      if (data.invited) toast.success(`Inbjudan skickad till ${u.email}`);
      else toast.warning(`Mejl misslyckades. Tillfälligt lösenord: ${data.temp_password}`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const edit = (u) => { setForm({ name: u.name, email: u.email, password: "", role: u.role }); setEditId(u.id); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Ta bort anställd?")) return; try { await api.delete(`/users/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="rise">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-500">Hantering</p>
          <h1 className="font-display text-3xl font-extrabold text-white">Personal</h1>
        </div>
        <Button data-testid="add-employee-button" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}>
          <Plus size={16} /> Lägg till anställd
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-800">
                <th className="px-5 py-3">Namn</th><th className="px-5 py-3">E-post</th>
                <th className="px-5 py-3">Roll</th><th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="team-table">
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2 font-medium text-white">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-800 text-slate-400"><User size={14} /></span>
                      {u.name}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-400">{u.email}</td>
                  <td className="px-5 py-3"><span className="capitalize text-slate-300">{roleLabel(u.role)}</span></td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {u.role !== "owner" && <Button variant="outline" className="px-2.5 py-1.5 text-xs" data-testid={`invite-employee-${u.id}`} onClick={() => invite(u)}><Mail size={13} /> Inbjudan</Button>}
                      {u.role !== "owner" && <Button variant="outline" className="px-2.5 py-1.5 text-xs" data-testid={`set-password-${u.id}`} onClick={() => openPw(u)}><KeyRound size={13} /> Lösenord</Button>}
                      <Button variant="outline" className="px-2.5 py-1.5 text-xs" data-testid={`edit-employee-${u.id}`} onClick={() => edit(u)}><Pencil size={13} /></Button>
                      {isOwner && u.role !== "owner" && <Button variant="danger" className="px-2.5 py-1.5 text-xs" data-testid={`delete-employee-${u.id}`} onClick={() => del(u.id)}><Trash2 size={13} /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Redigera anställd" : "Ny anställd"}>
        <form onSubmit={save} className="space-y-4">
          <div><Label>Namn</Label><Input data-testid="employee-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div><Label>E-post</Label><Input data-testid="employee-email-input" type="email" value={form.email} disabled={!!editId} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
          {editId ? (
            <div><Label>Nytt lösenord (valfritt)</Label><Input data-testid="employee-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          ) : (
            <p className="rounded-lg border border-slate-700 bg-slate-950/40 px-3 py-2.5 text-xs text-slate-400">Ett tillfälligt lösenord skapas automatiskt och mejlas till den anställde. De väljer sitt eget lösenord vid första inloggningen.</p>
          )}
          {editId && isOwner && (
            <div>
              <Label>Roll</Label>
              <select data-testid="employee-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full rounded-lg bg-slate-950/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-amber-500">
                <option value="employee">Anställd</option>
                <option value="partner">Delägare (ser allt, men kan inte ändra priser eller ta bort personal)</option>
                <option value="owner">Ägare (full behörighet)</option>
              </select>
            </div>
          )}
          <p className="text-xs text-slate-500">Timpris sätts på sidan <span className="text-amber-400">Priser</span>.</p>
          <Button data-testid="save-employee-button" type="submit" className="w-full">Spara</Button>
        </form>
      </Modal>

      <Modal open={!!pwUser} onClose={() => setPwUser(null)} title={`Sätt lösenord${pwUser ? " – " + pwUser.name : ""}`}>
        {!pwResult ? (
          <form onSubmit={setPassword} className="space-y-4">
            <p className="rounded-lg border border-slate-700 bg-slate-950/40 px-3 py-2.5 text-xs text-slate-400">
              Skriv ett lösenord eller lämna tomt för att skapa ett automatiskt. Lösenordet gäller direkt och visas här så att du kan ge det till den anställde. Inget mejl skickas.
            </p>
            <div>
              <Label>Lösenord (valfritt)</Label>
              <Input data-testid="set-password-input" type="text" value={pwValue} placeholder="Minst 6 tecken – lämna tomt för slumpat" onChange={(e) => setPwValue(e.target.value)} />
            </div>
            <Button data-testid="confirm-set-password-button" type="submit" disabled={pwBusy} className="w-full">
              <KeyRound size={16} /> {pwBusy ? "Sätter…" : "Sätt lösenord"}
            </Button>
          </form>
        ) : (
          <div className="space-y-4" data-testid="set-password-result">
            <p className="text-sm text-slate-300">Ge dessa uppgifter till <span className="font-semibold text-white">{pwResult.name}</span>:</p>
            <div className="rounded-lg border border-slate-700 bg-slate-950/40 p-4">
              <div className="mb-2 text-xs text-slate-500">E-post</div>
              <div className="mb-3 font-medium text-white">{pwResult.email}</div>
              <div className="mb-2 text-xs text-slate-500">Lösenord</div>
              <div className="flex items-center gap-2">
                <code data-testid="set-password-value" className="flex-1 rounded bg-slate-800 px-3 py-2 font-mono text-base text-amber-400">{pwResult.password}</code>
                <Button variant="outline" className="px-2.5 py-2" data-testid="copy-password-button" onClick={copyPw}>{copied ? <Check size={15} /> : <Copy size={15} />}</Button>
              </div>
            </div>
            <p className="text-xs text-slate-500">Den anställde kan logga in direkt med detta lösenord.</p>
            <Button variant="outline" className="w-full" data-testid="copy-credentials-button" onClick={copyCreds}>
              {credCopied ? <Check size={15} /> : <Copy size={15} />} {credCopied ? "Kopierat!" : "Kopiera inloggningsuppgifter"}
            </Button>
            <Button data-testid="close-set-password-button" className="w-full" onClick={() => setPwUser(null)}>Klar</Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
